import { useContext, useState } from 'react';
import { AdminTableContext } from '../../AdminTable';
import { useQueryClient } from '@tanstack/react-query';
import { AdminProfessionals } from '@platform/client';
import { InputDropdownItem, FormsInputDropdown, FormsCheckBox } from '@platform/components';
import {
  GetAdminProfessionalsParams,
  ProfessionalApplicationStatusEnum,
} from '@platform/client/schemas';
import { useForm, SubmitHandler } from 'react-hook-form';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { Alert } from '@platform/components';

type ProApplicationStatusForm = {
  status: ProfessionalApplicationStatusEnum | null;
  sendEmail: boolean;
};

export const SingleProAppStatusModal = (props: {
  onClose: () => void;
  applicationId: number;
  status: ProfessionalApplicationStatusEnum;
  userId: number;
  isSapPro?: boolean;
  queryKeyToInvalidate?: readonly (string | GetAdminProfessionalsParams)[];
}) => {
  return (
    <BaseAppStatusModal
      onClose={props.onClose}
      isBatch={false}
      applicationId={props.applicationId}
      status={props.status}
      userId={props.userId}
      isSapPro={props.isSapPro}
      queryKeyToInvalidate={props.queryKeyToInvalidate}
    />
  );
};

export const BatchProAppStatusModal = (props: {
  onClose: () => void;
  queryKeyToInvalidate?: readonly (string | GetAdminProfessionalsParams)[];
}) => {
  return (
    <BaseAppStatusModal
      onClose={props.onClose}
      isBatch={true}
      queryKeyToInvalidate={props.queryKeyToInvalidate}
    />
  );
};

const BaseAppStatusModal = (
  props: { onClose: () => void } & (
    | {
        isBatch: true;
        applicationId?: number;
        status?: ProfessionalApplicationStatusEnum;
        userId?: number;
        isSapPro?: boolean;
        queryKeyToInvalidate?: readonly (string | GetAdminProfessionalsParams)[];
      }
    | {
        isBatch: false;
        applicationId: number;
        status: ProfessionalApplicationStatusEnum;
        userId: number;
        isSapPro?: boolean;
        queryKeyToInvalidate?: readonly (string | GetAdminProfessionalsParams)[];
      }
  ),
) => {
  const { isBatch, applicationId, isSapPro = false } = props;
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { rowSelection } = useContext(AdminTableContext);

  const { control, watch, formState, handleSubmit } = useForm<ProApplicationStatusForm>({
    defaultValues: {
      status: null,
      sendEmail: false,
    },
  });
  const selectedStatus = watch('status');

  const queryClient = useQueryClient();
  const mutateOptions = {
    onMutate: () => {
      setModalStage('submitting');
    },
    onError: () => {
      setModalStage('error');
    },
    onSuccess: () => {
      setModalStage('success');
      if (props.queryKeyToInvalidate) {
        queryClient.invalidateQueries({ queryKey: props.queryKeyToInvalidate });
      }
    },
  };
  const singleAppStatusMutator = AdminProfessionals.useUpdateProApplicationStatus({
    mutation: {
      ...mutateOptions,
    },
  });
  const batchAppStatusMutator = AdminProfessionals.useUpdateProApplicationStatusBatch({
    mutation: {
      ...mutateOptions,
    },
  });

  const onSubmit: SubmitHandler<ProApplicationStatusForm> = (data) => {
    if (data.status) {
      if (isBatch) {
        const selectedUserIds = Object.keys(rowSelection ?? {}).map((key) => Number(key));
        batchAppStatusMutator.mutate({
          data: {
            userIds: selectedUserIds,
            status: data.status,
            sendEmail: data.sendEmail,
          },
        });
      } else {
        singleAppStatusMutator.mutate({
          data: {
            applicationId,
            status: data.status,
            sendEmail: data.sendEmail,
          },
        });
      }
    }
  };

  // Conditional form items. SAP UI only supports accept / reject states, so we filter what admins can assign
  const allowedStatuses: ProfessionalApplicationStatusEnum[] =
    isSapPro ?
      [ProfessionalApplicationStatusEnum.approved, ProfessionalApplicationStatusEnum.rejected]
    : [
        ProfessionalApplicationStatusEnum.waitlisted,
        ProfessionalApplicationStatusEnum.pending,
        ProfessionalApplicationStatusEnum.approved,
        ProfessionalApplicationStatusEnum.rejected,
      ];
  const resultItems: InputDropdownItem[] = Object.values(ProfessionalApplicationStatusEnum)
    .filter((status) => allowedStatuses.includes(status))
    .map((filteredStatus) => ({
      textValue: filteredStatus,
      text: filteredStatus.split('_').join(' '),
    }));

  // API only supports notification emails for approved, rejected, and waitlisted statuses
  const includeNotify =
    selectedStatus === ProfessionalApplicationStatusEnum.approved ||
    selectedStatus === ProfessionalApplicationStatusEnum.rejected ||
    selectedStatus === ProfessionalApplicationStatusEnum.waitlisted;

  return (
    <AdminFormModal
      onClose={props.onClose}
      title={
        isBatch ?
          `Batch application status: ${Object.keys(rowSelection ?? {}).length} records`
        : `User ${props.userId} application status change`
      }
      modalStage={modalStage}
      submitCTA={{
        text: 'Update status',
        callback: () => handleSubmit(onSubmit)(),
        buttonVariant: 'primary',
      }}
    >
      <div className='flex flex-col gap-4'>
        {!isBatch && (
          <>
            <p className='mb-2'>
              <span className='font-semibold'>Current status:</span>{' '}
              {props.status.replaceAll('_', ' ')}
            </p>
          </>
        )}
        {isBatch && (
          <Alert variant='info'>
            <p>
              Application status changes for Transform/Indivdual professionals are not supported.
              Only corporate professional applications will be updated with this change, regardless
              of selected users.
            </p>
          </Alert>
        )}
        <FormsInputDropdown
          type='default'
          inputDisabled
          control={control}
          name='status'
          label='New application status'
          items={resultItems}
          placeholder='Select a status'
          rules={{ required: 'Application status is required' }}
          error={formState.errors.status}
        />
        {includeNotify && (
          <div>
            <FormsCheckBox
              control={control}
              name='sendEmail'
              description='Notify professional'
              size='md'
            />
          </div>
        )}
      </div>
    </AdminFormModal>
  );
};
